import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { downloadReportCsv, fetchReport, type ReportParams, type ReportResult } from '../lib/api';
import { BranchPicker } from '../components/branch-picker';

/**
 * Phase 3 admin reports — a registry-driven picker. Pick a report, set the date
 * window (and a slot code / reason where the report needs one), view the table,
 * and export the exact same data as CSV. Every report shares one generic
 * `{ columns, rows, summary }` renderer.
 */
type ReportKind = 'window' | 'none' | 'member' | 'movements';
/** `branch` marks the reports the server narrows by `?branchId=` — it cuts
 *  across the kinds (Sales All is a window report, Stock on Hand isn't), so it's
 *  a flag rather than another kind. Must match BRANCH_WINDOW_REPORTS + the two
 *  inventory routes in apps/api/src/routes/reports.ts. */
type ReportDef = { key: string; label: string; group: string; kind: ReportKind; branch?: true };

const REPORTS: ReportDef[] = [
  { key: 'ecom-orders', label: 'Ecom Orders', group: 'Sales', kind: 'window', branch: true },
  { key: 'sales-all', label: 'Sales (All)', group: 'Sales', kind: 'window', branch: true },
  { key: 'product-movement', label: 'Product Movement', group: 'Sales', kind: 'window', branch: true },
  { key: 'payout', label: 'Payouts', group: 'Finance', kind: 'window' },
  { key: 'member-earnings', label: 'Member Earnings', group: 'Members', kind: 'window' },
  { key: 'member-wallet', label: 'Member Wallets', group: 'Members', kind: 'none' },
  { key: 'member-pv', label: 'Member Point Value', group: 'Members', kind: 'window' },
  { key: 'member-details', label: 'Member Details', group: 'Members', kind: 'window' },
  { key: 'member-detailed-wallet', label: 'Member Wallet Log', group: 'Members', kind: 'member' },
  { key: 'member-binary-points', label: 'Member Binary Points', group: 'Members', kind: 'member' },
  { key: 'referral', label: 'Referral Summary', group: 'Network', kind: 'window' },
  { key: 'direct-referral', label: 'Direct Referrals', group: 'Network', kind: 'window' },
  { key: 'downline-detail', label: 'Downline Detail', group: 'Network', kind: 'window' },
  { key: 'inventory-stock', label: 'Stock on Hand', group: 'Inventory', kind: 'none', branch: true },
  { key: 'inventory-movements', label: 'Stock Movements', group: 'Inventory', kind: 'movements', branch: true },
];

const GROUPS = ['Sales', 'Finance', 'Members', 'Network', 'Inventory'];

const MOVEMENT_REASONS = [
  'receive', 'adjustment', 'write_off', 'void', 'pos_sale', 'ecom_sale',
  'refund_restock', 'cancel_restock', 'stockist_purchase',
];

export function ReportsPage() {
  const [selectedKey, setSelectedKey] = useState<string>('ecom-orders');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [slotCode, setSlotCode] = useState('');
  const [reason, setReason] = useState('');
  const [branchId, setBranchId] = useState<string | undefined>();

  const def = REPORTS.find((r) => r.key === selectedKey)!;
  const needsSlot = def.kind === 'member';
  const showWindow = def.kind === 'window' || def.kind === 'movements';

  const params: ReportParams = {
    ...(showWindow && from ? { from: new Date(from).toISOString() } : {}),
    ...(showWindow && to ? { to: new Date(to).toISOString() } : {}),
    ...(needsSlot && slotCode ? { slotCode: slotCode.trim() } : {}),
    ...(def.kind === 'movements' && reason ? { reason } : {}),
    ...(def.branch && branchId ? { branchId } : {}),
  };
  const ready = !needsSlot || !!slotCode.trim();

  const reportQ = useQuery({
    queryKey: ['report', selectedKey, params],
    queryFn: () => fetchReport(selectedKey, params),
    enabled: ready,
  });

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_1fr]">
      {/* Report picker */}
      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="text-base">Reports</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {GROUPS.map((g) => (
            <div key={g} className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{g}</p>
              {REPORTS.filter((r) => r.group === g).map((r) => (
                <button
                  key={r.key}
                  onClick={() => {
                    setSelectedKey(r.key);
                    // Branch changes RESULTS, so it can't quietly carry over to a
                    // report that ignores it and then reapply on the way back.
                    if (!r.branch) setBranchId(undefined);
                  }}
                  className={
                    'block w-full rounded-md px-2 py-1 text-left text-sm transition-colors ' +
                    (r.key === selectedKey
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground')
                  }
                >
                  {r.label}
                </button>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Controls + results */}
      <div className="space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle>{def.label}</CardTitle>
            <Button
              variant="outline"
              size="sm"
              disabled={!ready || !reportQ.data?.rows.length}
              onClick={() => downloadReportCsv(selectedKey, params)}
            >
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-2">
              {showWindow ? (
                <>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">From</p>
                    <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">To</p>
                    <Input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
                  </div>
                  <Button variant="ghost" onClick={() => { setFrom(''); setTo(''); }}>Clear</Button>
                </>
              ) : null}
              {def.branch ? <BranchPicker value={branchId} onChange={setBranchId} /> : null}
              {needsSlot ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Slot code</p>
                  <Input placeholder="e.g. ADMELIC…" value={slotCode} onChange={(e) => setSlotCode(e.target.value)} />
                </div>
              ) : null}
              {def.kind === 'movements' ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Reason</p>
                  <select
                    className="h-10 rounded-md border bg-background px-3 text-sm"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  >
                    <option value="">All reasons</option>
                    {MOVEMENT_REASONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {!ready ? (
              <p className="p-6 text-sm text-muted-foreground">Enter a slot code to run this report.</p>
            ) : reportQ.isLoading ? (
              <p className="p-6 text-sm text-muted-foreground">Loading…</p>
            ) : reportQ.error ? (
              <p className="p-6 text-sm text-destructive">Failed to load the report.</p>
            ) : !reportQ.data?.rows.length ? (
              <p className="p-6 text-sm text-muted-foreground">No data for this report / range.</p>
            ) : (
              <ReportTable data={reportQ.data} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ReportTable({ data }: { data: ReportResult }) {
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground border-b">
            <tr>
              {data.columns.map((c) => (
                <th key={c.key} className={'px-3 py-2 font-medium ' + (c.align === 'right' ? 'text-right' : '')}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={i} className="border-b last:border-0">
                {data.columns.map((c) => (
                  <td
                    key={c.key}
                    className={'px-3 py-1.5 text-xs ' + (c.align === 'right' ? 'text-right font-mono tabular-nums' : '')}
                  >
                    {row[c.key] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.summary?.length ? (
        <div className="flex flex-wrap gap-x-6 gap-y-1 border-t px-3 py-3 text-xs">
          {data.summary.map((s) => (
            <span key={s.label}>
              <span className="text-muted-foreground">{s.label}: </span>
              <span className="font-medium tabular-nums">{s.value}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
